import { useState } from 'react';
import { useAuth } from '@/state/auth';
import {
  useESignCreate,
  useESignPublicKey,
  useESignVerify,
  useESignatures,
  useRecipeTransition,
  useRecipeTransitions,
  useRecipes,
} from '@/lib/query';
import type { ESignature, Recipe, RecipeTransition } from '@shared/domain';
import { ts } from '@/lib/format';
import { Card, DataTable } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-ok-bg text-ok-fg border-ok-border',
  draft: 'bg-slate-100 text-slate-500 border-slate-200',
  pending: 'bg-warn-bg text-warn-fg border-warn-border',
  approved: 'bg-info-bg text-info-fg border-info-border',
  archived: 'bg-slate-100 text-slate-400 border-slate-200',
};
const STATUS_LABEL: Record<string, string> = { active: '激活', draft: '草稿', pending: '待审批', approved: '已批准', archived: '归档' };
const TARGET: Record<string, string> = { submit: 'pending', approve: 'approved', activate: 'active', archive: 'archived' };

function VerifyTag({ id }: { id: string | null }) {
  const { data } = useESignVerify(id);
  if (!id) return <span className="text-slate-400">—</span>;
  if (!data) return <span className="text-slate-400">校验中…</span>;
  return <span className={data.valid ? 'text-ok-fg' : 'text-alarm-fg'}>{data.valid ? '签名有效' : '签名失效'}</span>;
}

function TransitionsPanel({ recipeId }: { recipeId: string }) {
  const { data } = useRecipeTransitions(recipeId);
  return (
    <Card className="p-5">
      <div className="mb-3 text-[15px] font-semibold text-slate-800">流转轨迹 · {recipeId}</div>
      <DataTable
        columns={[
          { key: 'action', header: '动作', render: (t: RecipeTransition) => <span>{t.action}</span> },
          { key: 'flow', header: '状态', render: (t: RecipeTransition) => <span className="text-slate-500">{STATUS_LABEL[t.fromStatus]} → {STATUS_LABEL[t.toStatus]}</span> },
          { key: 'actor', header: '操作人' },
          { key: 'at', header: '时间', render: (t: RecipeTransition) => <span className="tabular-nums text-slate-500">{ts(t.at, false)}</span> },
          { key: 'sig', header: '签名', render: (t: RecipeTransition) => <VerifyTag id={t.signatureId} /> },
        ]}
        rows={data ?? []}
        empty={<div className="py-4 text-center text-[13px] text-slate-400">暂无流转</div>}
      />
    </Card>
  );
}

export default function Recipes() {
  const { user } = useAuth();
  const { data: recipes } = useRecipes();
  const { data: sigs } = useESignatures();
  const { data: pk } = useESignPublicKey();
  const transition = useRecipeTransition();
  const createSig = useESignCreate();
  const [msg, setMsg] = useState<string | null>(null);
  const [sel, setSel] = useState<string | null>(null);

  const role = user?.role ?? '';
  const canEdit = ['engineer', 'supervisor', 'admin'].includes(role);
  const canApprove = ['supervisor', 'admin'].includes(role);

  const act = async (r: Recipe, action: 'submit' | 'approve' | 'activate' | 'archive') => {
    setMsg(null);
    try {
      let signatureId: string | undefined;
      if (action === 'approve' || action === 'activate') {
        const pw = window.prompt(`二次认证：输入账户口令以${action === 'approve' ? '审批' : '激活'} ${r.id}`);
        if (!pw) return;
        const sig = await createSig.mutateAsync({
          action: action === 'approve' ? 'recipe.approve' : 'recipe.activate',
          resourceType: 'recipe',
          resourceId: r.id,
          meaning: `${STATUS_LABEL[TARGET[action]]} ${r.name}`,
          payload: { toStatus: TARGET[action], version: r.version },
          password: pw,
        });
        signatureId = sig.id;
      }
      await transition.mutateAsync({ id: r.id, action, signatureId, ifMatch: r.etag });
      setMsg(`${action} 成功：${r.id} → ${STATUS_LABEL[TARGET[action]]}`);
    } catch (e) {
      setMsg(`${action} 失败：${(e as Error).message}`);
    }
  };

  return (
    <div>
      <PageBanner />
      <div className="mb-6">
        <h2 className="flex items-center gap-2 text-[24px] font-bold text-slate-900">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-slate-100 text-slate-500">R</span>
          配方生命周期（PR7）
        </h2>
        <p className="mt-1 text-[14px] text-slate-500">草稿→提交→审批→激活→归档；审批/激活须携带 PR6 电子签名，乐观锁（eTag）并发保护。</p>
      </div>

      {msg ? <div className="mb-4 rounded-lg bg-info-bg px-4 py-2 text-[13px] text-info-fg">{msg}</div> : null}

      <Card className="mb-6 p-6">
        <div className="mb-3 text-[16px] font-semibold text-slate-800">配方目录</div>
        <DataTable
          columns={[
            { key: 'id', header: '编号', render: (r: Recipe) => <span className="font-mono text-[13px]">{r.id}</span> },
            { key: 'name', header: '名称' },
            { key: 'status', header: '状态', render: (r: Recipe) => <span className={`pill ${STATUS_STYLE[r.status]}`}>{STATUS_LABEL[r.status]}</span> },
            { key: 'version', header: '版本', render: (r: Recipe) => <span className="tabular-nums">{r.version}</span> },
            { key: 'etag', header: 'ETAG', render: (r: Recipe) => <span className="font-mono text-[12px] text-slate-400">{r.etag}</span> },
            {
              key: 'ops',
              header: '操作',
              render: (r: Recipe) => (
                <div className="flex flex-wrap items-center gap-2 text-[13px]">
                  {r.status === 'draft' && canEdit ? <button onClick={() => act(r, 'submit')} className="text-brand-600 hover:underline">提交</button> : null}
                  {r.status === 'pending' && canApprove ? <button onClick={() => act(r, 'approve')} className="text-brand-600 hover:underline">审批</button> : null}
                  {r.status === 'approved' && canApprove ? <button onClick={() => act(r, 'activate')} className="text-brand-600 hover:underline">激活</button> : null}
                  {r.status === 'active' && canApprove ? <button onClick={() => act(r, 'archive')} className="text-slate-500 hover:underline">归档</button> : null}
                  <button onClick={() => setSel(r.id)} className="text-slate-500 hover:underline">轨迹</button>
                </div>
              ),
            },
          ]}
          rows={recipes ?? []}
        />
        <div className="mt-3 text-[12px] text-slate-400">审批/激活会先弹出二次口令认证，生成一条覆盖该动作的电子签名后再流转（PR6↔PR7 打通）。</div>
      </Card>

      {sel ? (
        <div className="mb-6">
          <TransitionsPanel recipeId={sel} />
        </div>
      ) : null}

      <Card className="p-6">
        <div className="mb-3 text-[16px] font-semibold text-slate-800">电子签名账本（PR6）</div>
        <DataTable
          columns={[
            { key: 'seq', header: '#', render: (s: ESignature) => <span className="tabular-nums">{s.seq}</span> },
            { key: 'action', header: '动作', render: (s: ESignature) => <span className="font-mono text-[12px]">{s.action}</span> },
            { key: 'resourceId', header: '对象', render: (s: ESignature) => <span className="font-mono text-[12px]">{s.resourceId}</span> },
            { key: 'signer', header: '签署人', render: (s: ESignature) => <span>{s.signerName}</span> },
            { key: 'signedAt', header: '时间', render: (s: ESignature) => <span className="tabular-nums text-slate-500">{ts(s.signedAt, false)}</span> },
            { key: 'verify', header: '验签', render: (s: ESignature) => <VerifyTag id={s.id} /> },
          ]}
          rows={sigs ?? []}
          empty={<div className="py-4 text-center text-[13px] text-slate-400">暂无签名（当前角色无权查看或尚无签名）</div>}
        />
        {pk ? (
          <details className="mt-3">
            <summary className="cursor-pointer text-[13px] text-slate-500">验签公钥（{pk.algorithm}{pk.ephemeral ? ' · 临时密钥' : ''}）</summary>
            <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-50 p-3 text-[11px] text-slate-500">{pk.publicKeyPem}</pre>
          </details>
        ) : null}
      </Card>
    </div>
  );
}
